import L from "leaflet";
import { useEffect, useRef, useState } from "react";
import {
	MapContainer,
	Marker,
	Polygon,
	TileLayer,
	Tooltip,
	useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import type {
	SmartField_Api_Contracts_CoordinateDto,
	SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto,
	SmartField_Api_Contracts_ExperimentalLocation_SiteTreatmentDto,
	SmartField_Api_Contracts_Mapping_ChamberMappingDto,
	SmartField_Api_Contracts_Mapping_TowerMappingDto,
	SmartField_Api_Contracts_Sensor_DimSensorDto,
	SmartField_Api_Contracts_Site_SiteDto,
} from "../../api";
import type { PageMode } from "./SiteGeometryDevicesPage";
import { MULTI_CHANNEL_DEVICES } from "./sensorConstants";
import { PlotTooltip } from "../shared/PlotTooltip";

const PLOT_COLORS = [
	"#3b82f6",
	"#f97316",
	"#a855f7",
	"#14b8a6",
	"#ef4444",
	"#0ea5e9",
];
const FIELD_COLOR = "#f59e0b";

function coordsToLatLng(
	coords: SmartField_Api_Contracts_CoordinateDto[],
): L.LatLngTuple[] {
	return coords.map((c) => [c.y ?? 0, c.x ?? 0] as L.LatLngTuple);
}


function avgCoord(
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[],
	fn: (s: SmartField_Api_Contracts_Sensor_DimSensorDto) => number,
): number {
	const vals = sensors.map(fn);
	return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function makeElPinIcon(color: string): L.DivIcon {
	return L.divIcon({
		className: "",
		html: `<div style="width:20px;height:20px;border-radius:4px;background:${color};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-size:9px;color:white;font-weight:bold">?</div>`,
		iconSize: [20, 20],
		iconAnchor: [10, 10],
	});
}

function makeSensorIcon(color: string): L.DivIcon {
	return L.divIcon({
		className: "",
		html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4)"></div>`,
		iconSize: [14, 14],
		iconAnchor: [7, 7],
	});
}

function makeHighlightedSensorIcon(color: string): L.DivIcon {
	return L.divIcon({
		className: "",
		html: `<div style="width:22px;height:22px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 0 0 3px ${color},0 2px 6px rgba(0,0,0,0.5);animation:sensor-highlight 0.6s ease-in-out infinite alternate"></div><style>@keyframes sensor-highlight{from{transform:scale(1)}to{transform:scale(1.35)}}</style>`,
		iconSize: [22, 22],
		iconAnchor: [11, 11],
	});
}

function makePendingSensorIcon(color: string): L.DivIcon {
	return L.divIcon({
		className: "",
		html: `<div style="width:20px;height:20px;border-radius:50%;background:transparent;border:3px solid ${color};box-shadow:0 1px 4px rgba(0,0,0,0.5);animation:pending-pulse 1s ease-in-out infinite alternate"></div><style>@keyframes pending-pulse{from{opacity:1}to{opacity:0.4}}</style>`,
		iconSize: [20, 20],
		iconAnchor: [10, 10],
	});
}

const DEVICE_COLORS: Record<string, string> = {};
const PALETTE = [
	"#3b82f6",
	"#f97316",
	"#a855f7",
	"#14b8a6",
	"#ef4444",
	"#0ea5e9",
	"#84cc16",
	"#f43f5e",
];
let colorIdx = 0;

function deviceColor(serialKey: string): string {
	if (!DEVICE_COLORS[serialKey]) {
		DEVICE_COLORS[serialKey] = PALETTE[colorIdx % PALETTE.length] ?? "#3b82f6";
		colorIdx++;
	}
	return DEVICE_COLORS[serialKey] ?? "#3b82f6";
}

function FitBoundsToData({
	plots,
	fields,
	blocks,
	miniBlocks,
	boundary,
	sensors,
	siteLocation,
}: {
	plots: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	fields: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	blocks: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	miniBlocks: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	boundary: L.LatLngTuple[] | null;
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[];
	siteLocation: L.LatLngTuple | null;
}) {
	const map = useMap();
	const fitted = useRef(false);

	useEffect(() => {
		if (fitted.current) {
			return;
		}

		const allCoords: L.LatLngTuple[] = [];

		if (boundary) {
			allCoords.push(...boundary);
		}
		for (const p of plots) {
			if (p.shapeCoordinates && p.shapeCoordinates.length > 0) {
				allCoords.push(...coordsToLatLng(p.shapeCoordinates));
			}
		}
		for (const f of fields) {
			if (f.shapeCoordinates && f.shapeCoordinates.length > 0) {
				allCoords.push(...coordsToLatLng(f.shapeCoordinates));
			}
		}
		for (const b of blocks) {
			if (b.shapeCoordinates && b.shapeCoordinates.length > 0) {
				allCoords.push(...coordsToLatLng(b.shapeCoordinates));
			}
		}
		for (const mb of miniBlocks) {
			if (mb.shapeCoordinates && mb.shapeCoordinates.length > 0) {
				allCoords.push(...coordsToLatLng(mb.shapeCoordinates));
			}
		}

		if (allCoords.length === 0) {
			for (const s of sensors) {
				if (
					s.currentGeolocation?.y != null &&
					s.currentGeolocation?.x != null
				) {
					allCoords.push([s.currentGeolocation.y, s.currentGeolocation.x]);
				}
			}
		}

		if (allCoords.length === 0) {
			if (siteLocation) {
				map.setView(siteLocation, 15);
				// Don't mark as fitted — geometry may arrive later and should trigger a proper fitBounds.
			}
			return;
		}

		fitted.current = true;
		map.fitBounds(L.latLngBounds(allCoords), { padding: [30, 30] });
	}, [map, plots, fields, blocks, miniBlocks, boundary, sensors, siteLocation]);

	return null;
}

export type FocusTarget = { elId: number; ts: number };
export type FocusChannelTarget = { channelKey: string; ts: number };

function PanToChannel({
	target,
	sensors,
}: {
	target: FocusChannelTarget | null;
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[];
}) {
	const map = useMap();
	const prevTs = useRef<number | null>(null);

	useEffect(() => {
		if (!target || target.ts === prevTs.current) {
			return;
		}
		prevTs.current = target.ts;

		const channelSensors = sensors.filter((s) => {
			const key = `${s.deviceSerialKey ?? "unknown"}|${s.channelNumber ?? 0}`;
			return key === target.channelKey && s.currentGeolocation?.y != null && s.currentGeolocation?.x != null;
		});
		if (channelSensors.length === 0) {
			return;
		}
		const lat = avgCoord(channelSensors, (s) => s.currentGeolocation?.y ?? 0);
		const lng = avgCoord(channelSensors, (s) => s.currentGeolocation?.x ?? 0);
		map.setView([lat, lng], Math.max(map.getZoom(), 17));
	}, [target, map, sensors]);

	return null;
}

function PanToTarget({
	target,
	plots,
	fields,
	blocks,
	miniBlocks,
	sensors,
}: {
	target: FocusTarget | null;
	plots: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	fields: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	blocks: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	miniBlocks: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[];
}) {
	const map = useMap();
	const prevTs = useRef<number | null>(null);

	useEffect(() => {
		if (!target || target.ts === prevTs.current) {
			return;
		}
		prevTs.current = target.ts;

		const el = [...plots, ...fields, ...blocks, ...miniBlocks].find((e) => e.id === target.elId);
		if (!el) {
			return;
		}

		if (el.shapeCoordinates && el.shapeCoordinates.length >= 3) {
			map.fitBounds(L.latLngBounds(coordsToLatLng(el.shapeCoordinates)), {
				padding: [40, 40],
			});
			return;
		}

		const assigned = sensors.filter(
			(s) =>
				s.currentExperimentalLocationId === el.id &&
				s.currentGeolocation?.y != null &&
				s.currentGeolocation?.x != null,
		);
		if (assigned.length > 0) {
			const lat = avgCoord(assigned, (s) => s.currentGeolocation?.y ?? 0);
			const lng = avgCoord(assigned, (s) => s.currentGeolocation?.x ?? 0);
			map.setView([lat, lng], Math.max(map.getZoom(), 16));
			return;
		}
	}, [target, map, plots, fields, blocks, miniBlocks, sensors]);

	return null;
}

type Props = {
	site: SmartField_Api_Contracts_Site_SiteDto | null;
	asOf: string;
	isViewingPast: boolean;
	plots: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	fields: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	blocks: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	miniBlocks: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[];
	chambers: SmartField_Api_Contracts_Mapping_ChamberMappingDto[];
	towers: SmartField_Api_Contracts_Mapping_TowerMappingDto[];
	siteTreatments: SmartField_Api_Contracts_ExperimentalLocation_SiteTreatmentDto[];
	visibleLayers: Set<string>;
	mode: PageMode;
	canEdit: boolean;
	focusTarget: FocusTarget | null;
	focusChannelTarget: FocusChannelTarget | null;
	highlightedLocationId: number | null;
	highlightedChannelKey: string | null;
	placingCoords: SmartField_Api_Contracts_CoordinateDto | null;
	placingSensors: SmartField_Api_Contracts_Sensor_DimSensorDto[];
	onSensorDrop: (
		channelSensors: SmartField_Api_Contracts_Sensor_DimSensorDto[],
		coords: SmartField_Api_Contracts_CoordinateDto,
	) => void;
	onSensorDragEnd: (
		channelSensors: SmartField_Api_Contracts_Sensor_DimSensorDto[],
		coords: SmartField_Api_Contracts_CoordinateDto,
	) => void;
	onSensorClick: (
		channelSensors: SmartField_Api_Contracts_Sensor_DimSensorDto[],
	) => void;
	onElClick: (
		el: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto,
	) => void;
};

function DragDropHandler({
	sensors,
	onSensorDrop,
}: {
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[];
	onSensorDrop: Props["onSensorDrop"];
}) {
	const map = useMap();

	useEffect(() => {
		const container = map.getContainer();

		const onDragOver = (e: DragEvent) => e.preventDefault();
		const onDrop = (e: DragEvent) => {
			e.preventDefault();
			const channelIds = e.dataTransfer?.getData("channel-sensor-ids");
			if (!channelIds) return;
			const idSet = new Set(channelIds.split(","));
			const channelSensors = sensors.filter((s) => idSet.has(String(s.id)));
			if (channelSensors.length === 0) return;
			const rect = container.getBoundingClientRect();
			const point = L.point(e.clientX - rect.left, e.clientY - rect.top);
			const latlng = map.containerPointToLatLng(point);
			onSensorDrop(channelSensors, { x: latlng.lng, y: latlng.lat });
		};

		container.addEventListener("dragover", onDragOver);
		container.addEventListener("drop", onDrop);
		return () => {
			container.removeEventListener("dragover", onDragOver);
			container.removeEventListener("drop", onDrop);
		};
	}, [map, sensors, onSensorDrop]);

	return null;
}



export function GeometryDevicesCanvas({
	site,
	asOf,
	isViewingPast,
	plots,
	fields,
	blocks,
	miniBlocks,
	sensors,
	chambers,
	siteTreatments,
	visibleLayers,
	canEdit,
	focusTarget,
	focusChannelTarget,
	highlightedLocationId,
	highlightedChannelKey,
	placingCoords,
	placingSensors,
	onSensorDrop,
	onSensorDragEnd,
	onSensorClick,
	onElClick,
}: Props) {
	const [draggingChannel, setDraggingChannel] = useState<string | null>(null);

	const siteLocation: L.LatLngTuple | null =
		site?.location?.y != null && site?.location?.x != null
			? [site.location.y, site.location.x]
			: null;

	const center: L.LatLngTuple = siteLocation ?? [55.5, 10.0];

	// Mobile sites relocate, and Site.Shape is current-only, so it's wrong for past dates.
	const siteShapeCoordinates = isViewingPast && site?.siteType === "Mobile" ? null : site?.shapeCoordinates;
	const boundaryCoords =
		siteShapeCoordinates && siteShapeCoordinates.length > 0
			? coordsToLatLng(siteShapeCoordinates)
			: null;

	const placedSensors = sensors.filter(
		(s) => s.id !== -1 && s.currentGeolocation?.y && s.currentGeolocation?.x,
	);

	// Group placed sensors by device+channel so one marker represents the whole channel
	// Key format must match LayerSidebar groupByDeviceAndChannel: "deviceKey|all" for multi-channel devices
	const placedChannelGroups: Map<string, typeof placedSensors> = new Map();
	for (const s of placedSensors) {
		const chKey = MULTI_CHANNEL_DEVICES.has(s.deviceFriendlyName ?? "")
			? "all"
			: String(s.channelNumber ?? 0);
		const key = `${s.deviceSerialKey ?? "unknown"}|${chKey}`;
		const group = placedChannelGroups.get(key) ?? [];
		group.push(s);
		placedChannelGroups.set(key, group);
	}

	return (
		<MapContainer
			center={center}
			zoom={15}
			style={{ height: "100%", width: "100%" }}
			className="z-0"
		>
			<TileLayer
				attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
				url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				maxZoom={22}
				maxNativeZoom={19}
			/>

			<FitBoundsToData
				key={asOf}
				plots={plots}
				fields={fields}
				blocks={blocks}
				miniBlocks={miniBlocks}
				boundary={boundaryCoords}
				sensors={sensors}
				siteLocation={siteLocation}
			/>
			<PanToTarget
				target={focusTarget}
				plots={plots}
				fields={fields}
				blocks={blocks}
				miniBlocks={miniBlocks}
				sensors={sensors}
			/>
			<PanToChannel target={focusChannelTarget} sensors={sensors} />

			{visibleLayers.has("boundary") && boundaryCoords && (
				<Polygon
					positions={boundaryCoords}
					pathOptions={{ color: "#22c55e", weight: 2, fillOpacity: 0.08 }}
				/>
			)}

			{visibleLayers.has("plots") &&
				(() => {
					const treatmentColorMap = new Map<number | string, string>();
					let colorIdx = 0;
					for (let i = 0; i < plots.length; i++) {
						const plot = plots[i]!;
						const key = plot.treatmentId ?? plot.entityIdentifier ?? plot.id ?? i;
						if (!treatmentColorMap.has(key)) {
							treatmentColorMap.set(key, PLOT_COLORS[colorIdx++ % PLOT_COLORS.length] ?? "#3b82f6");
						}
					}
					return plots.map((plot, index) => {
					const color = treatmentColorMap.get(plot.treatmentId ?? plot.entityIdentifier ?? plot.id ?? index) ?? "#3b82f6";
					const highlighted = highlightedLocationId === plot.id;
					const plotLabel =
						plot.friendlyName ?? plot.entityIdentifier ?? `Plot ${plot.plotId}`;
					const treatmentNumber = siteTreatments.find((t) => t.id === plot.treatmentId)?.friendlyName ?? null;
					const plotChamberNumbers = chambers
						.filter((c) => c.trialId === plot.fieldId && c.plotNumber === plot.plotId)
						.map((c) => c.chamberNumber)
						.filter((n): n is number => n != null);
					const chamberNumber = plotChamberNumbers.length > 0 ? plotChamberNumbers.join(", ") : null;
					const plotAssignedSensors = sensors.filter(
						(s) => s.currentExperimentalLocationId === plot.id,
					);
					const hasT54 = plotAssignedSensors.some((s) => s.deviceSerialKey?.startsWith("T54"));
					const hasT32 = plotAssignedSensors.some((s) => s.deviceSerialKey?.startsWith("T32"));
					if (plot.shapeCoordinates && plot.shapeCoordinates.length >= 3) {
						const coords = plot.shapeCoordinates;
						return (
							<Polygon
								key={plot.id}
								positions={coordsToLatLng(coords)}
								pathOptions={
									highlighted
										? {
												color: "#f59e0b",
												weight: 5,
												fillColor: "#fde68a",
												fillOpacity: 0.55,
											}
										: { color, weight: 2, fillOpacity: 0.15 }
								}
								eventHandlers={{ click: () => onElClick(plot) }}
							>
								<Tooltip sticky>
									<PlotTooltip
										plotName={plotLabel}
										plotNumber={plot.plotId}
										treatmentNumber={treatmentNumber}
										chamberNumber={chamberNumber}
										hasT54={hasT54}
										hasT32={hasT32}
									/>
								</Tooltip>
							</Polygon>
						);
					}
					const assignedSensors = plotAssignedSensors.filter(
						(s) =>
							s.currentGeolocation?.y != null &&
							s.currentGeolocation?.x != null,
					);
					if (assignedSensors.length === 0) {
						return null;
					}
					const pinLat = avgCoord(
						assignedSensors,
						(s) => s.currentGeolocation?.y ?? 0,
					);
					const pinLng = avgCoord(
						assignedSensors,
						(s) => s.currentGeolocation?.x ?? 0,
					);
					return (
						<Marker
							key={`plot-pin-${plot.id ?? plot.entityIdentifier}`}
							position={[pinLat, pinLng]}
							icon={makeElPinIcon(highlighted ? "#fbbf24" : color)}
							eventHandlers={{ click: () => onElClick(plot) }}
						>
							<Tooltip>
								<PlotTooltip
									plotName={`${plotLabel} (no boundary drawn)`}
									plotNumber={plot.plotId}
									treatmentNumber={treatmentNumber}
									chamberNumber={chamberNumber}
									hasT54={hasT54}
									hasT32={hasT32}
								/>
							</Tooltip>
						</Marker>
					);
				});
				})()}

			{visibleLayers.has("fields") &&
				fields.map((field, fieldIdx) => {
					const highlighted = highlightedLocationId === field.id;
					const fieldLabel =
						field.friendlyName ??
						field.entityIdentifier ??
						`Field ${field.fieldId}`;
					if (field.shapeCoordinates && field.shapeCoordinates.length >= 3) {
						const coords = field.shapeCoordinates;
						return (
							<Polygon
								key={`field-${field.id ?? fieldIdx}`}
								positions={coordsToLatLng(coords)}
								pathOptions={
									highlighted
										? {
												color: "#f59e0b",
												weight: 5,
												fillColor: "#fde68a",
												fillOpacity: 0.55,
											}
										: { color: FIELD_COLOR, weight: 2, fillOpacity: 0.12 }
								}
								eventHandlers={{ click: () => onElClick(field) }}
							>
								<Tooltip sticky>{fieldLabel}</Tooltip>
							</Polygon>
						);
					}
					const assignedSensors = sensors.filter(
						(s) =>
							s.currentExperimentalLocationId === field.id &&
							s.currentGeolocation?.y != null &&
							s.currentGeolocation?.x != null,
					);
					if (assignedSensors.length === 0) {
						return null;
					}
					const pinLat = avgCoord(
						assignedSensors,
						(s) => s.currentGeolocation?.y ?? 0,
					);
					const pinLng = avgCoord(
						assignedSensors,
						(s) => s.currentGeolocation?.x ?? 0,
					);
					return (
						<Marker
							key={`field-pin-${field.id ?? fieldIdx}`}
							position={[pinLat, pinLng]}
							icon={makeElPinIcon(highlighted ? "#fbbf24" : FIELD_COLOR)}
							eventHandlers={{ click: () => onElClick(field) }}
						>
							<Tooltip>{fieldLabel} (no boundary drawn)</Tooltip>
						</Marker>
					);
				})}

			{visibleLayers.has("sensors") &&
				Array.from(placedChannelGroups.entries()).map(
					([channelKey, channelSensors]) => {
						const representative = channelSensors[0];
						if (!representative) return null;
						const color = deviceColor(representative.deviceSerialKey ?? "");
						const isHighlighted = highlightedChannelKey === channelKey;
						const isDragging = draggingChannel === channelKey;
						const icon = isHighlighted || isDragging
							? makeHighlightedSensorIcon(color)
							: makeSensorIcon(color);
						const geo = representative.currentGeolocation;
						if (!geo?.y || !geo?.x) return null;
						const pos: L.LatLngTuple = [geo.y, geo.x];
						const draggable = canEdit;

						const tooltipContent = [
							representative.friendlyName ??
								`Channel ${representative.channelNumber}`,
							representative.deviceFriendlyName ??
								representative.deviceSerialKey,
							representative.manufacturer,
							representative.deviceFriendlyName
								? `Serial: ${representative.deviceSerialKey}`
								: null,
						]
							.filter(Boolean)
							.join("\n");

						return (
							<Marker
								key={channelKey}
								position={pos}
								icon={icon}
								draggable={draggable}
								eventHandlers={{
									click: () => onSensorClick(channelSensors),
									dragstart: draggable ? () => setDraggingChannel(channelKey) : undefined,
									dragend: draggable
										? (e) => {
												setDraggingChannel(null);
												const ll = (e.target as L.Marker).getLatLng();
												onSensorDragEnd(channelSensors, {
													x: ll.lng,
													y: ll.lat,
												});
											}
										: undefined,
								}}
							>
								<Tooltip>
									<div style={{ whiteSpace: "pre-line", fontSize: "12px" }}>
										{tooltipContent}
									</div>
								</Tooltip>
							</Marker>
						);
					},
				)}

			{placingCoords?.y != null &&
				placingCoords?.x != null &&
				placingSensors.length > 0 && (
					<Marker
						position={[placingCoords.y, placingCoords.x]}
						icon={makePendingSensorIcon(
							deviceColor(placingSensors[0]?.deviceSerialKey ?? ""),
						)}
					>
						<Tooltip permanent>
							{placingSensors[0]?.friendlyName ??
								`Channel ${placingSensors[0]?.channelNumber} — pending`}
						</Tooltip>
					</Marker>
				)}

			{visibleLayers.has("blocks") &&
				blocks.map((block, blockIdx) => {
					const blockLabel = block.friendlyName ?? `Block ${block.blockId}`;
					const highlighted = highlightedLocationId === block.id;
					if (block.shapeCoordinates && block.shapeCoordinates.length >= 3) {
						return (
							<Polygon
								key={`block-${block.id ?? blockIdx}`}
								positions={coordsToLatLng(block.shapeCoordinates)}
								pathOptions={
									highlighted
										? { color: "#f59e0b", weight: 5, fillColor: "#fde68a", fillOpacity: 0.55 }
										: { color: "#a855f7", weight: 2, fillOpacity: 0.12 }
								}
								eventHandlers={{ click: () => onElClick(block) }}
							>
								<Tooltip sticky>{blockLabel}</Tooltip>
							</Polygon>
						);
					}
					return null;
				})}

			{visibleLayers.has("miniblocks") &&
				miniBlocks.map((mb, mbIdx) => {
					const mbLabel = mb.friendlyName ?? `Mini-block ${mb.miniBlockId}`;
					const highlighted = highlightedLocationId === mb.id;
					if (mb.shapeCoordinates && mb.shapeCoordinates.length >= 3) {
						return (
							<Polygon
								key={`miniblock-${mb.id ?? mbIdx}`}
								positions={coordsToLatLng(mb.shapeCoordinates)}
								pathOptions={
									highlighted
										? { color: "#f59e0b", weight: 5, fillColor: "#fde68a", fillOpacity: 0.55 }
										: { color: "#14b8a6", weight: 2, fillOpacity: 0.12 }
								}
								eventHandlers={{ click: () => onElClick(mb) }}
							>
								<Tooltip sticky>{mbLabel}</Tooltip>
							</Polygon>
						);
					}
					return null;
				})}

			<DragDropHandler sensors={sensors} onSensorDrop={onSensorDrop} />
		</MapContainer>
	);
}
